import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InstanceCard, EmptyState } from "../../components/dashboard";
import { getMyInstance, ApiError } from "../../lib/api/cloud";
import type { Instance } from "../../types/cloud";

export function Dashboard() {
  const { t } = useTranslation();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [noInstance, setNoInstance] = useState(false);

  const fetchInstance = async () => {
    try {
      const data = await getMyInstance();
      setInstance(data);
      setNoInstance(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNoInstance(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstance();
  }, []);

  // Poll for status updates when instance is in a transitional state
  useEffect(() => {
    if (
      !instance ||
      !["provisioning", "starting", "stopping", "deleting"].includes(instance.status)
    ) {
      return;
    }

    const interval = setInterval(fetchInstance, 5000);
    return () => clearInterval(interval);
  }, [instance?.status, instance?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">{t('dashboard.title')}</h1>
        <p className="text-[#999] text-sm mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {noInstance ? (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-8">
          <EmptyState
            icon={
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            }
            title={t('dashboard.noInstance.title')}
            description={t('dashboard.noInstance.description')}
          />
        </div>
      ) : instance ? (
        <>
          <InstanceCard instance={instance} onRefresh={fetchInstance} />
          {instance.status === "running" && (
            <div className="mt-4">
              <Link
                to="/dashboard/import"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#f5f5f5] text-sm font-medium hover:border-[#555] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {t('dashboard.importProject')}
              </Link>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
